import { useTranslation } from '../../../i18n/useTranslation';
import type { LicenseFilterState } from '../../../types/manageLicenses';

interface Props {
  selectionCount: number;
  totalCount: number;
  isPageFullySelected: boolean;
  filters: LicenseFilterState;
  onOpenDialog: (kind: 'revoke' | 'suspend' | 'reactivate' | 'extend') => void;
  onExport: () => void;
  onSelectAllMatchingFilter: () => Promise<void>;
  onClearSelection: () => void;
  permissions: { canRevoke: boolean; canSuspend: boolean; canManage: boolean };
}

export default function LicenseBulkActionsBar({
  selectionCount, totalCount, isPageFullySelected,
  onOpenDialog, onExport, onSelectAllMatchingFilter, onClearSelection,
  permissions,
}: Props) {
  const { t } = useTranslation();
  if (selectionCount === 0) {
    return (
      <div className="flex items-center justify-between mb-3 text-sm text-neutral-600">
        <span>{t('App.Admin.Licenses.LicenseBulkActionsBar.TotalCount', { count: totalCount })}</span>
        <button
          type="button"
          onClick={onExport}
          className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100"
        >
          {t('App.Admin.Licenses.LicenseBulkActionsBar.ExportToExcel')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3 p-2 bg-primary-50 border border-primary-200 rounded text-sm">
      <div className="flex items-center gap-3">
        <span>{t('App.Admin.Licenses.LicenseBulkActionsBar.SelectedCount', { count: selectionCount })}</span>
        {isPageFullySelected && selectionCount < totalCount && (
          <button
            type="button"
            onClick={onSelectAllMatchingFilter}
            className="text-primary-600 hover:underline"
          >
            {t('App.Admin.Licenses.LicenseBulkActionsBar.SelectAllFilter', { count: Math.min(totalCount, 500) })}
          </button>
        )}
        <button type="button" onClick={onClearSelection} className="text-neutral-600 hover:underline">
          {t('App.Admin.Licenses.LicenseBulkActionsBar.Clear')}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {permissions.canRevoke && (
          <button
            type="button"
            onClick={() => onOpenDialog('revoke')}
            className="px-3 py-1 bg-white border border-red-300 text-red-700 rounded hover:bg-red-50"
          >
            {t('App.Admin.Licenses.LicenseBulkActionsBar.Revoke')}
          </button>
        )}
        {permissions.canSuspend && (
          <button
            type="button"
            onClick={() => onOpenDialog('suspend')}
            className="px-3 py-1 bg-white border border-amber-300 text-amber-700 rounded hover:bg-amber-50"
          >
            {t('App.Admin.Licenses.LicenseBulkActionsBar.Suspend')}
          </button>
        )}
        {permissions.canManage && (
          <button
            type="button"
            onClick={() => onOpenDialog('reactivate')}
            className="px-3 py-1 bg-white border border-neutral-300 rounded hover:bg-neutral-100"
          >
            {t('App.Admin.Licenses.LicenseBulkActionsBar.Reactivate')}
          </button>
        )}
        {permissions.canManage && (
          <button
            type="button"
            onClick={() => onOpenDialog('extend')}
            className="px-3 py-1 bg-white border border-neutral-300 rounded hover:bg-neutral-100"
          >
            {t('App.Admin.Licenses.LicenseBulkActionsBar.Extend')}
          </button>
        )}
        <button
          type="button"
          onClick={onExport}
          className="px-3 py-1 bg-white border border-neutral-300 rounded hover:bg-neutral-100"
        >
          {t('App.Admin.Licenses.LicenseBulkActionsBar.Export')}
        </button>
      </div>
    </div>
  );
}
